package com.essntl.features.itinerary.domain.use_case

import com.essntl.core.utils.viewmodel.Operation
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.toastmanager.IO
import com.essntl.toastmanager.ToastManager
import kotlin.coroutines.cancellation.CancellationException
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import org.koin.core.annotation.Single
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.withContext

@Single
class UpdateItineraryServiceUseCase(
    private val itineraryRepository: ItineraryRepository,
    private val toastManager: ToastManager,
) {
    data class UseCaseState(
        val loadingSet: Set<String> = emptySet(),
    )

    private val _state = MutableStateFlow(UseCaseState())
    val state = _state.asStateFlow()

    private val _successEvent = MutableSharedFlow<Operation.Update<ItineraryServiceModel>>()
    val successEvent = _successEvent.asSharedFlow()

    private val _failureEvent = MutableSharedFlow<Throwable>()
    val failureEvent = _failureEvent.asSharedFlow()

    suspend fun update(
        itineraryService: ItineraryServiceModel,
        ignoreLoading: Boolean = false,
        showSuccessToast: Boolean = true,
        onSuccess: (ItineraryServiceModel) -> Unit = {},
    ) {
        try {
            if (itineraryService.id in _state.value.loadingSet && !ignoreLoading)
                return

            if (itineraryService.isEmpty)
                return

            _state.update {
                it.copy(
                    loadingSet = _state.value.loadingSet + itineraryService.id
                )
            }

            itineraryRepository.updateItineraryService(itineraryService)
                .onSuccess { newItineraryService ->
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - itineraryService.id
                        )
                    }

                    _successEvent.emit(Operation.Update(newItineraryService))
                    onSuccess(newItineraryService)

                    if (showSuccessToast)
                        toastManager.success(description = "Itinerary updated successfully")
                }
                .onFailure { e ->
                    e.printStackTrace()
                    _state.update {
                        it.copy(
                            loadingSet = _state.value.loadingSet - itineraryService.id
                        )
                    }

                    _failureEvent.emit(e)
                }
        } catch (e: CancellationException) {
            _state.update {
                it.copy(
                    loadingSet = it.loadingSet - itineraryService.id
                )
            }

            _failureEvent.emit(e)
        }
    }

    suspend fun updateBatch(
        itineraryServices: List<ItineraryServiceModel>,
        onSuccess: (suspend () -> Unit)? = null
    ): Result<List<ItineraryServiceModel>> = runCatching {
        if (itineraryServices.isEmpty()) return@runCatching emptyList()

        itineraryRepository.updateItineraryServiceStatuses(itineraryServices)
            .onSuccess { updatedServices ->
                updatedServices.forEach { service ->
                    _successEvent.emit(Operation.Update(service))
                }
                onSuccess?.invoke()
            }
            .getOrThrow()
    }
}
